import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";
import { useT, LanguageSwitcher } from "@/lib/i18n";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowRight, ArrowLeft, Lock, Gamepad2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";

export default function LoginPage() {
  const { emailLogin, registerEmail } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();

  const [mode, setMode] = useState("signin"); // "signin" | "create"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [staySignedIn, setStaySignedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH. Use window.location.origin dynamically.
  // Google login temporarily disabled per user request (2026-02-18). Handler
  // kept for easy re-enable; the button has been removed from the UI.
  // eslint-disable-next-line no-unused-vars
  const handleGoogle = () => {
    setLoading(true);
    const redirectUrl = window.location.origin + "/app";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const handleApple = () => {
    toast.info(t("login.apple_soon") || "Apple Login kommt bald.");
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!email || !password) {
      setFormError(t("login.enter_both") || "Bitte E-Mail und Passwort angeben.");
      return;
    }
    if (mode === "create" && password.length < 8) {
      setFormError(t("login.pw_short") || "Passwort: mind. 8 Zeichen, 1 Buchstabe + 1 Zahl.");
      return;
    }
    setLoading(true);
    try {
      const fn = mode === "create" ? registerEmail : emailLogin;
      const { profile } = await fn({ email, password, staySignedIn });
      toast.success(
        mode === "create"
          ? (t("login.welcome_new") || "Account erstellt. Los geht's, Pilot.")
          : (t("login.welcome") || "Welcome back, Pilot.")
      );
      // Always push to onboarding if incomplete — the public gaming identity
      // must be picked by the user, NEVER imported from the email prefix.
      if (!profile?.completed_onboarding) {
        navigate("/onboarding");
      } else {
        navigate("/app");
      }
    } catch (err) {
      // Build a friendly message from the most specific source available.
      // Order: backend `detail` string → axios `message` (Network/timeout) →
      // generic fallback. Without this order, a Cloudflare 5xx or DNS error
      // would be silently swallowed and only the generic toast would show.
      const detailRaw = formatApiErrorDetail(err?.response?.data?.detail);
      const status = err?.response?.status;
      const axiosMsg = err?.message;
      let friendly;
      if (detailRaw === "email_exists" || detailRaw === "email_taken") {
        friendly = "Diese E-Mail ist bereits registriert. Stattdessen einloggen?";
      } else if (detailRaw === "invalid_credentials") {
        friendly = "E-Mail oder Passwort falsch.";
      } else if (detailRaw === "account_deactivated") {
        friendly = "Dieses Konto wurde deaktiviert.";
      } else if (detailRaw === "not_authenticated" || detailRaw === "auth_required") {
        friendly = "Deine Sitzung ist abgelaufen — bitte erneut einloggen.";
      } else if (detailRaw === "too_many_attempts") {
        friendly = "Zu viele Versuche. Bitte etwas warten und erneut versuchen.";
      } else if (detailRaw) {
        friendly = detailRaw;
      } else if (err?.code === "ECONNABORTED" || /timeout/i.test(axiosMsg || "")) {
        friendly = "Der Server brauchte zu lange — bitte gleich nochmal versuchen.";
      } else if (axiosMsg === "Network Error") {
        friendly = "Verbindung zum Server fehlgeschlagen. Bitte Internet prüfen.";
      } else if (status >= 500) {
        friendly = "Serverfehler — bitte gleich nochmal versuchen.";
      } else {
        friendly = axiosMsg || "Da ist etwas schiefgelaufen. Bitte erneut versuchen.";
      }
      setFormError(friendly);
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-radial-neon grid lg:grid-cols-2"
      data-testid="login-page"
    >
      {/* Visual pane */}
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
        <Logo to="/" />
        <div className="relative">
          <h2 className="font-display text-4xl font-black leading-[1.05]">
            {t("login.welcome_back_h")}
            <br />
            <span className="text-neon text-neon-glow">Pilot.</span>
          </h2>
          <p className="mt-4 text-zinc-400 max-w-md">{t("login.welcome_back_sub")}</p>
          <div className="mt-10 relative">
            <img
              src="https://customer-assets.emergentagent.com/job_pilot-gaming-hub/artifacts/wy19r611_3AA32CCB-D3AE-4966-AA8F-F78E3977C651.png"
              alt="FicoPilot"
              className="w-80 drop-shadow-[0_0_60px_rgba(0,229,255,0.45)]"
            />
          </div>
        </div>
        <p className="text-xs text-zinc-500 relative">{t("footer.tagline")}</p>
      </div>

      {/* Form pane */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
              data-testid="login-back"
            >
              <ArrowLeft size={14} /> FicoPilot
            </Link>
            <LanguageSwitcher />
          </div>

          <div className="lg:hidden mb-8 flex justify-center">
            <Logo to="/" />
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
            {mode === "create"
              ? (t("login.create_h") || "Create your ")
              : (t("login.signin_h") || "Sign in to ")}
            <span className="text-neon">FicoPilot</span>
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            {mode === "create"
              ? (t("login.create_sub") || "Build your gamer identity. Pick a unique nickname next — no real names exposed.")
              : (t("login.signin_sub") || "Jump back into your squad, your builds, your coach.")}
          </p>

          {/* Mode tabs */}
          <div
            className="mt-6 grid grid-cols-2 p-1 rounded-xl bg-white/5 border border-white/10"
            data-testid="login-mode-tabs"
          >
            <button
              type="button"
              onClick={() => { setMode("signin"); setFormError(null); }}
              className={`h-10 rounded-lg text-sm font-semibold transition ${
                mode === "signin" ? "bg-neon text-black shadow-neon" : "text-zinc-300"
              }`}
              data-testid="login-tab-signin"
            >
              {t("login.tab_signin") || "Sign in"}
            </button>
            <button
              type="button"
              onClick={() => { setMode("create"); setFormError(null); }}
              className={`h-10 rounded-lg text-sm font-semibold transition ${
                mode === "create" ? "bg-neon text-black shadow-neon" : "text-zinc-300"
              }`}
              data-testid="login-tab-create"
            >
              {t("login.tab_create") || "Create account"}
            </button>
          </div>

          {/* Social providers — Google temporarily disabled per user request (2026-02-18).
              Re-enable: restore <Button onClick={handleGoogle}> above the Apple button
              and add the handler back (see git history). */}
          <div className="mt-6 flex flex-col gap-3">
            <Button
              variant="outline"
              className="h-12 rounded-xl bg-black text-white hover:bg-zinc-900 border border-white/10 font-semibold gap-3 opacity-60"
              onClick={handleApple}
              disabled={loading}
              data-testid="login-apple-btn"
              title={t("login.apple_soon") || "Bald verfügbar"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.365 1.43c0 1.14-.43 2.18-1.17 2.98-.88.95-2.3 1.67-3.45 1.58-.13-1.1.42-2.25 1.12-2.99.78-.83 2.24-1.52 3.5-1.57zm3.7 17.49c-.78 1.14-1.6 2.28-2.89 2.3-1.27.02-1.68-.75-3.12-.75s-1.89.73-3.1.77c-1.24.05-2.18-1.23-2.96-2.36-1.6-2.3-2.83-6.51-1.18-9.36.82-1.41 2.29-2.3 3.88-2.33 1.23-.03 2.39.82 3.13.82.74 0 2.15-1.02 3.63-.87.62.02 2.35.25 3.47 1.87-.09.06-2.06 1.2-2.04 3.57.02 2.83 2.47 3.77 2.5 3.78-.02.05-.39 1.34-1.32 2.56z" />
              </svg>
              {t("login.apple")}
            </Button>
          </div>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px bg-white/10 flex-1" />
            <span className="text-xs uppercase tracking-widest text-zinc-500">{t("login.or")}</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          {/* Email form */}
          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4" data-testid="login-email-form">
            <div>
              <Label htmlFor="email" className="text-zinc-300">
                {t("login.email")}
              </Label>
              <div className="mt-1.5 relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@ficopilot.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-10 rounded-xl bg-zinc-900 border-white/10 focus-visible:border-neon focus-visible:ring-neon"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-zinc-300">
                  {t("login.password")}
                </Label>
                {mode === "signin" && (
                  <Link
                    to="/forgot-password"
                    className="text-xs text-neon hover:text-white transition"
                    data-testid="login-forgot-link"
                  >
                    {t("login.forgot") || "Passwort vergessen?"}
                  </Link>
                )}
              </div>
              <div className="mt-1.5 relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete={mode === "create" ? "new-password" : "current-password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-10 pr-10 rounded-xl bg-zinc-900 border-white/10 focus-visible:border-neon focus-visible:ring-neon"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                  data-testid="login-password-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {mode === "create" && (
                <p className="mt-1 text-[11px] text-zinc-500">
                  {t("login.pw_hint") || "Mind. 8 Zeichen, 1 Buchstabe, 1 Zahl."}
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-xs text-zinc-400 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={staySignedIn}
                onChange={(e) => setStaySignedIn(e.target.checked)}
                className="accent-neon h-4 w-4"
                data-testid="login-stay-signed-in"
              />
              {t("login.stay_signed_in") || "Angemeldet bleiben"}
            </label>

            {formError && (
              <div
                className="text-xs rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2"
                data-testid="login-form-error"
              >
                {formError}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="h-12 rounded-xl bg-neon text-black font-bold hover:bg-neon-hover shadow-neon"
              data-testid="login-submit-btn"
            >
              {loading
                ? (t("login.signing_in") || "Wird geladen …")
                : mode === "create"
                ? (t("login.create_cta") || "Create account")
                : (t("login.submit") || "Sign in")}
              <ArrowRight size={18} className="ml-1" />
            </Button>
          </form>

          <div className="mt-5 flex items-center gap-2 text-[11px] text-zinc-500 justify-center">
            <Gamepad2 size={12} className="text-neon/70" />
            <span>
              {t("login.identity_note") ||
                "Dein Gamer-Nickname wird im nächsten Schritt gewählt. Dein echter Name bleibt privat."}
            </span>
          </div>

          <p className="mt-4 text-xs text-zinc-500 text-center">{t("login.terms")}</p>
          <Footer variant="compact" />
        </div>
      </div>
    </div>
  );
}
